import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types'
import { TrackChangeType } from 'models/changes'

/** Case-insensitive DELETED check — server emits PascalCase `'Deleted'`. */
export const isDeletedChange = (changeType?: string | null): boolean =>
    changeType?.toUpperCase() === TrackChangeType.DELETED

/** True if any visible article matches `text` (case-insensitive, trimmed). */
export const isDuplicateArticle = (
    text: string,
    contentNodes: Partial<ContentItemViewNodesType>[]
): boolean => {
    if (!contentNodes || !Array.isArray(contentNodes)) {
        return false
    }

    const articles = contentNodes.filter(
        (node) => node.textType === 'Article' && !node.isHidden
    )
    const normalizedText = text.trim().toLowerCase()

    return articles.some((node) => {
        if (node.elements?.[0]?.text?.trim().toLowerCase() === normalizedText) {
            return true
        }

        if (node.elements && node.elements.length > 1) {
            const fullText = node.elements
                .filter((element) => !isDeletedChange(element.changeType))
                .map((element) => element.text || '')
                .join('')
                .trim()
                .toLowerCase()

            return fullText === normalizedText
        }

        return false
    })
}

const TRAILING_PUNCTUATION_RE = /[.,;:!?]+$/

/** Lowercase, trim, strip trailing punctuation, trim again (the strip can
 *  expose new trailing whitespace, e.g. `"My Key . "` → `"my key"`). */
const normalizeKey = (key: string): string =>
    key
        .toLowerCase()
        .trim()
        .replace(TRAILING_PUNCTUATION_RE, '')
        .trim()

/** Extracts the normalized key (text before the first colon) from a paragraph's
 *  elements, skipping DELETED tombstones and concatenating across elements. */
export const extractParagraphKey = (
    elements: Partial<TextContentElement>[]
): string => {
    let key = ''
    let foundColon = false

    for (const element of elements) {
        if (!element?.text || isDeletedChange(element.changeType)) continue
        const text = element.text

        if (text.startsWith(':')) {
            foundColon = true
            break
        }

        const colonIdx = text.indexOf(':')
        if (colonIdx !== -1) {
            key += text.substring(0, colonIdx)
            foundColon = true
            break
        }
        key += text
    }
    return foundColon ? normalizeKey(key) : ''
}

/** True if `keyText` matches any non-hidden sibling's key (case-insensitive). */
export const isDuplicateParagraphKey = (
    keyText: string,
    siblings: Partial<ContentItemViewNodesType>[]
): boolean => {
    const normalized = normalizeKey(keyText)

    if (!normalized) return false

    return siblings.some((sibling) => {
        if (sibling.isHidden) return false

        const siblingKey = extractParagraphKey(sibling.elements ?? [])
        return siblingKey === normalized
    })
}
